import { useEffect, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa6';
import NavbarLink from '../layout/links/NavbarLink';

const isBrowser = typeof window !== 'undefined';

export default function DropDownNavbar({
    label,
    href = '#',
    handleCloseNavbar,
    navbar = true,
    linkClassName = '',
    children,
    // Controlled accordion props (optional)
    isOpen: controlledIsOpen,
    onToggle,
}) {
    const [internalIsOpen, setInternalIsOpen] = useState(false);

    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = isControlled ? onToggle : setInternalIsOpen;

    const hasChildren = children && (Array.isArray(children) ? children.filter(Boolean).length > 0 : true);
    const chevronColorClass = linkClassName.includes('text-white') ? 'text-white' : 'text-gray-700';

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-wrapper')) {
                if (!isControlled) setInternalIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen, isControlled]);

    const toggleDropdown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleMouseEnter = () => {
        if (isBrowser && window.innerWidth >= 1320 && hasChildren) {
            if (!isControlled) setInternalIsOpen(true);
        }
    };

    const handleMouseLeave = () => {
        if (isBrowser && window.innerWidth >= 1320) {
            if (!isControlled) setInternalIsOpen(false);
        }
    };

    const handleItemClick = () => {
        if (!isControlled) setInternalIsOpen(false);
        if (handleCloseNavbar) handleCloseNavbar();
    };

    const styledChildren = children ? (
        Array.isArray(children) ? (
            children.map((child, index) => {
                if (!child) return null;
                return (
                    <div
                        key={index}
                        onClick={handleItemClick}
                        className="py-1 text-[15px] font-medium transition-all duration-200 cursor-pointer"
                    >
                        {child}
                    </div>
                );
            })
        ) : (
            <div
                onClick={handleItemClick}
                className="py-1 text-[15px] font-medium transition-all duration-200 cursor-pointer"
            >
                {children}
            </div>
        )
    ) : null;

    if (!hasChildren) {
        return navbar === true ? (
            <NavbarLink href={href} onClick={handleCloseNavbar} className={linkClassName}>
                {label}
            </NavbarLink>
        ) : (
            <NavbarLink
                href={href}
                onClick={handleCloseNavbar}
                className="text-xs font-semibold tracking-wider text-gray-800 uppercase transition-colors duration-300 font-montserrat hover:text-custom-orange-600"
            >
                {label}
            </NavbarLink>
        );
    }

    return navbar === true ? (
        <div className="inline-block relative dropdown-wrapper dropdown-navbar-wrapper w-full lg:w-auto" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <style jsx global>{`
                .dropdown-navbar-wrapper,
                .dropdown-navbar-row,
                .dropdown-mobile-row {
                    width: 100% !important;
                }
                .dropdown-desktop-label,
                .dropdown-desktop-content {
                    display: none !important;
                }
                .dropdown-mobile-label,
                .dropdown-mobile-content {
                    display: block !important;
                }
                .dropdown-navbar-panel {
                    position: static !important;
                    width: 100% !important;
                    min-width: 0 !important;
                    margin-top: 0.5rem !important;
                    border-radius: 0 !important;
                    border: 0 !important;
                    background: transparent !important;
                    box-shadow: none !important;
                }
                .dropdown-navbar-panel.dropdown-panel-open {
                    max-height: calc(100vh - 160px);
                    overflow-y: auto;
                    opacity: 1;
                    visibility: visible;
                }
                .dropdown-navbar-panel.dropdown-panel-closed {
                    max-height: 0;
                    overflow: hidden;
                    opacity: 0;
                    visibility: hidden;
                }
                @media (min-width: 1320px) {
                    .dropdown-navbar-wrapper,
                    .dropdown-navbar-row,
                    .dropdown-mobile-row {
                        width: auto !important;
                    }
                    .dropdown-desktop-label {
                        display: inline-block !important;
                    }
                    .dropdown-mobile-label,
                    .dropdown-mobile-content {
                        display: none !important;
                    }
                    .dropdown-desktop-content {
                        display: block !important;
                    }
                    .dropdown-navbar-panel {
                        position: absolute !important;
                        top: 100% !important;
                        left: 0 !important;
                        width: auto !important;
                        min-width: 260px !important;
                        margin-top: 0.5rem !important;
                        border-radius: 1rem !important;
                        border: 1px solid rgba(255, 255, 255, 0.2) !important;
                        background: rgba(255, 255, 255, 0.8) !important;
                        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
                    }
                    .dropdown-navbar-panel.dropdown-panel-open {
                        max-height: 600px;
                        overflow: hidden;
                        transform: translateY(0);
                    }
                    .dropdown-navbar-panel.dropdown-panel-closed {
                        transform: translateY(-0.5rem);
                    }
                }
            `}</style>
            <div className="flex gap-2 items-center justify-between w-full lg:w-auto dropdown-navbar-row">
                {/* Desktop: label link + chevron side by side */}
                <NavbarLink href={href} onClick={handleCloseNavbar} className={`hidden lg:inline-block dropdown-desktop-label ${linkClassName}`}>
                    {label}
                </NavbarLink>

                {/* Mobile: label + chevron full row */}
                <div className="flex gap-2 justify-between items-center w-full lg:w-auto dropdown-mobile-row">
                    <NavbarLink href={href} onClick={handleCloseNavbar} className={`lg:hidden dropdown-mobile-label ${linkClassName}`}>
                        {label}
                    </NavbarLink>
                    <button
                        type="button"
                        onClick={toggleDropdown}
                        className="flex justify-center items-center transition-all duration-200"
                        aria-expanded={isOpen}
                        aria-label={`Open ${label} menu`}
                    >
                        <FaChevronDown className={`${chevronColorClass} transition-all duration-200 size-3.5 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
                    </button>
                </div>
            </div>

            {/* Dropdown Panel */}
            <div
                className={`
                    lg:absolute lg:top-full lg:left-0
                    w-full lg:w-auto lg:min-w-[260px]
                    mt-2 lg:mt-2
                    lg:rounded-2xl
                    transition-all duration-200 ease-out
                    lg:backdrop-blur-xl lg:bg-white/80
                    lg:shadow-[0_8px_30px_rgba(0,0,0,0.12)]
                    lg:border lg:border-white/20
                    dropdown-navbar-panel
                    ${isOpen ? 'dropdown-panel-open' : 'dropdown-panel-closed'}
                `}
                style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
            >
                {/* Mobile: only left border, no background */}
                <div className="lg:hidden dropdown-mobile-content mt-1 ml-2">
                    {styledChildren}
                </div>
                {/* Desktop: normal panel */}
                <div className="hidden lg:block dropdown-desktop-content py-2">{styledChildren}</div>
            </div>
        </div>
    ) : (
        // navbar=false variant unchanged
        <div className="relative dropdown-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button
                type="button"
                onClick={toggleDropdown}
                className="flex gap-2 justify-between items-center w-full text-xs font-semibold tracking-wider text-gray-800 uppercase transition-colors duration-200 lg:w-auto font-montserrat hover:text-custom-orange-600 group"
                aria-expanded={isOpen}
                aria-label={`Open ${label} menu`}
            >
                <span>{label}</span>
                <FaChevronDown className={`text-gray-700 group-hover:text-custom-orange-600 transition-all duration-200 size-3.5 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
            </button>
            <div
                className={`
                    lg:absolute lg:top-full lg:left-0
                    w-full lg:w-auto lg:min-w-[260px]
                    mt-3 lg:mt-2
                    lg:rounded-2xl
                    transition-all duration-200 ease-out
                    lg:backdrop-blur-xl lg:bg-white/80
                    lg:shadow-[0_8px_30px_rgba(0,0,0,0.12)]
                    lg:border lg:border-white/20
                    ${isOpen ? 'max-h-[600px] opacity-100 visible lg:translate-y-0' : 'max-h-0 overflow-hidden opacity-0 invisible lg:-translate-y-2'}
                `}
                style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
            >
                <div className="py-2">{styledChildren}</div>
            </div>
        </div>
    );
}
